import i18next from "i18next";
import get from "lodash/get";

import type { DiagnosticFact } from "src/api/client-api/diagnosticFacts/useDiagnosticFacts";
import type { FoodpilotContextInitialValues, UserContext } from "src/context/FoodpilotContext";
import type { StrategyInitialValues } from "src/context/Strategy/context";
import type { PuckActionPlanContextValue } from "src/components/Puck/config/contexts/PuckActionPlanContext";
import { calculatePercentageDifference } from "src/components/Puck/config/blocks/ActionPlan/utils/formatters";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { formatDateTime, formatNumber } from "src/utils/formatting";

type TokenScoreContext = {
    selectedVariation?: {
        scoreId?: number;
        variationId?: number | null;
    };
};

export type CalcFactMap = Record<string, DiagnosticFact>;

export type TokenResolverContexts = {
    user: UserContext["trustedUser"] | {};
    company: FoodpilotContextInitialValues["selectedCompany"] | {};
    campaign: FoodpilotContextInitialValues["currentCampaign"] | {};
    actionPlan: PuckActionPlanContextValue | {};
    strategyData: StrategyInitialValues | {};
    scores: TokenScoreContext | Record<string, unknown> | {};
    calc: CalcFactMap | {};
};

type TokenResolverContextsInput = Partial<TokenResolverContexts> & {
    actionsPlan?: PuckActionPlanContextValue | {};
    calcFacts?: CalcFactMap;
};

export type ProcessTokenOptions = {
    precision?: number;
    missingActionPlanValue?: string;
};

type TokenMatchContext = {
    locale: string;
    timeZone?: string;
};

export const TOKEN_REGEX = /\{\{\s*([^{}]+?)\s*\}\}|\{([^{}]+)\}/g;
const ACTION_PLAN_NAMESPACES = new Set(["actionPlan", "actionsPlan", "action_plan"]);

export const extractParameterKeys = (text: string | undefined): string[] => {
    if (!text) {
        return [];
    }

    const keys = new Set<string>();

    for (const match of text.matchAll(TOKEN_REGEX)) {
        const token = String(match[1] ?? match[2] ?? "").trim();
        if (token !== "") {
            keys.add(token);
        }
    }

    return [...keys].sort((a, b) => a.localeCompare(b));
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === "object" && value !== null && !Array.isArray(value);
};

const getBrowserTimeZone = (): string | undefined => {
    if (globalThis.window === undefined) return undefined;

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof timeZone === "string" && timeZone.trim() !== "" ? timeZone : undefined;
};

const getStoredLocale = (): string | undefined => {
    if (globalThis.window === undefined) {
        return i18next.resolvedLanguage || i18next.language || undefined;
    }

    const storedLanguage = localStorage.getItem("foodpilot_user_language");
    if (typeof storedLanguage === "string" && storedLanguage.trim() !== "") {
        return storedLanguage;
    }

    const cachedLanguages = localStorage.getItem("foodpilot_languages");
    if (typeof cachedLanguages === "string" && cachedLanguages.trim() !== "") {
        try {
            const parsed = JSON.parse(cachedLanguages) as { defaultLanguage?: unknown };
            if (typeof parsed.defaultLanguage === "string" && parsed.defaultLanguage.trim() !== "") {
                return parsed.defaultLanguage;
            }
        } catch {
            return i18next.resolvedLanguage || i18next.language || undefined;
        }
    }

    return i18next.resolvedLanguage || i18next.language || undefined;
};

const resolveLocale = (): string => {
    return getStoredLocale() || "en";
};

const resolveTimeZone = (): string | undefined => {
    return getBrowserTimeZone();
};

const extractYear = (value: unknown): string => {
    if (typeof value === "number" && Number.isInteger(value)) {
        return String(value);
    }

    if (typeof value !== "string") {
        return "";
    }

    const match = /(?:19|20)\d{2}/.exec(value);
    if (match) {
        return match[0];
    }

    const parsedDate = new Date(value);
    if (!Number.isNaN(parsedDate.getTime())) {
        return String(parsedDate.getUTCFullYear());
    }

    return "";
};

const formatGenericNumber = (value: number, precision: number, locale: string): string => {
    return new Intl.NumberFormat(locale, {
        maximumFractionDigits: precision,
        minimumFractionDigits: 0,
    }).format(value);
};

const formatSignedPercentageDifference = (value: number, locale: string): string => {
    if (!Number.isFinite(value)) {
        return "";
    }

    let sign = "";
    if (value > 0) {
        sign = "+";
    } else if (value < 0) {
        sign = "-";
    }

    const formattedValue = formatNumber(Math.abs(value / 100), locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        style: "percent",
        notation: "compact",
        withPrecisionFloor: false,
    });

    return `${sign} ${formattedValue}`;
};

const missingActionPlanPlaceholder = "—";

const normalizeArrayIndexPlaceholders = (path: string): string => {
    return path.replaceAll("[]", "[0]");
};

const formatGeneratedAt = (tokenMatchContext: TokenMatchContext): string => {
    return formatDateTime(new Date(), tokenMatchContext.locale, {
        dateStyle: "short",
        timeStyle: "medium",
        timeZone: tokenMatchContext.timeZone,
    });
};

const getActionPlanEntries = (contexts: TokenResolverContexts) => {
    const actionPlanContext = contexts.actionPlan;
    if (!isRecord(actionPlanContext)) return [];

    const actionPlanData = actionPlanContext.actionPlanData;
    return Array.isArray(actionPlanData) ? actionPlanData : [];
};

const getSelectedVariation = (contexts: TokenResolverContexts) => {
    const scoreContext = contexts.scores;
    if (!isRecord(scoreContext)) return undefined;

    const selectedVariation = scoreContext.selectedVariation;
    if (!isRecord(selectedVariation)) return undefined;

    const scoreId = selectedVariation.scoreId;
    const variationId = selectedVariation.variationId;

    if (typeof scoreId !== "number") {
        return undefined;
    }

    return {
        scoreId,
        variationId: typeof variationId === "number" ? variationId : null,
    };
};

const getActiveActionPlan = (contexts: TokenResolverContexts) => {
    const actionPlans = getActionPlanEntries(contexts);
    if (actionPlans.length === 0) {
        return undefined;
    }

    const selectedVariation = getSelectedVariation(contexts);
    if (!selectedVariation) {
        return actionPlans[0];
    }

    return (
        actionPlans.find((plan) => {
            if (!isRecord(plan)) return false;
            const scoreId = plan.scoreId;
            const variationId = plan.variationId ?? null;

            return scoreId === selectedVariation.scoreId && variationId === selectedVariation.variationId;
        }) || actionPlans[0]
    );
};

const getActionPlanForSelectedScore = (contexts: TokenResolverContexts) => {
    const actionPlans = getActionPlanEntries(contexts);
    const selectedScoreId = get(contexts.scores, "selectedVariation.scoreId");

    if (typeof selectedScoreId !== "number") {
        return getActiveActionPlan(contexts);
    }

    return (
        actionPlans.find((plan) => {
            return isRecord(plan) && plan.scoreId === selectedScoreId;
        }) || getActiveActionPlan(contexts)
    );
};

const getActionPlanPercentageDifference = (contexts: TokenResolverContexts): number | undefined => {
    const activeActionPlan = getActionPlanForSelectedScore(contexts);
    const selectedScoreId = get(contexts.scores, "selectedVariation.scoreId");
    const activePlanScoreId = get(activeActionPlan, "scoreId");
    const scoreId =
        typeof selectedScoreId === "number" ? selectedScoreId
        : typeof activePlanScoreId === "number" ? activePlanScoreId
        : undefined;

    if (!activeActionPlan || typeof scoreId !== "number") {
        return undefined;
    }

    const diagnosticRawScore = get(contexts.scores, `companyPost.olapScores.${scoreId}.score.value`);
    const diagnosticScore = OlapScoresService.normalizeNumericalScore(diagnosticRawScore, 0);

    const scoreDetails = get(activeActionPlan, "metadata.scoresWithActionPost.details");
    const normalizedScoreDetails = Array.isArray(scoreDetails) ? scoreDetails : [];

    const projectedRawScore =
        get(
            normalizedScoreDetails.find((detail) => {
                return get(detail, "score2.id") === scoreId;
            }),
            "score",
        ) ||
        get(activeActionPlan, "metadata.scoresWithActionPost.lastItem.score") ||
        diagnosticRawScore ||
        0;

    const projectedScore = OlapScoresService.normalizeNumericalScore(projectedRawScore, 0);
    const percentageDifference = calculatePercentageDifference(projectedScore, diagnosticScore);

    return Number.isFinite(percentageDifference) ? percentageDifference : undefined;
};

const resolveComputedToken = (
    token: string,
    contexts: TokenResolverContexts,
    tokenMatchContext: TokenMatchContext,
    options: ProcessTokenOptions,
): string | undefined => {
    const activeActionPlan = getActiveActionPlan(contexts);

    switch (token) {
        case "pdf.generated_at":
            return formatGeneratedAt(tokenMatchContext);
        case "pdf.current_year":
            return String(new Date().getFullYear());
        case "pdf.short_date":
            return formatDateTime(new Date(), tokenMatchContext.locale, {
                dateStyle: "short",
                timeZone: tokenMatchContext.timeZone,
            });
        case "pdf.medium_date":
            return formatDateTime(new Date(), tokenMatchContext.locale, {
                dateStyle: "medium",
                timeZone: tokenMatchContext.timeZone,
            });
        case "pdf.full_date":
            return formatDateTime(new Date(), tokenMatchContext.locale, {
                dateStyle: "full",
                timeZone: tokenMatchContext.timeZone,
            });
        case "campaign.current_year": {
            const campaignYear =
                extractYear(get(contexts.campaign, "label")) || extractYear(get(contexts.campaign, "startDate"));
            return campaignYear;
        }
        case "actionPlan.initial_diagnostic_year":
        case "actionsPlan.initial_diagnostic_year":
        case "action_plan.initial_diagnostic_year": {
            if (!activeActionPlan) {
                return missingActionPlanPlaceholder;
            }

            const initialDiagnosticYear = extractYear(get(activeActionPlan, "metadata.startingPost.campaign.label"));
            return initialDiagnosticYear || missingActionPlanPlaceholder;
        }
        case "actionPlan.reduction_percentage":
        case "actionsPlan.reduction_percentage":
        case "action_plan.reduction_percentage": {
            if (!getActionPlanForSelectedScore(contexts)) {
                return missingActionPlanPlaceholder;
            }

            const percentageDifference = getActionPlanPercentageDifference(contexts);
            if (typeof percentageDifference !== "number") {
                return missingActionPlanPlaceholder;
            }

            const formattedPercentageDifference = formatSignedPercentageDifference(
                percentageDifference,
                tokenMatchContext.locale,
            );

            return formattedPercentageDifference || missingActionPlanPlaceholder;
        }
        default:
            return undefined;
    }
};

const normalizeNamespace = (namespace: string): keyof TokenResolverContexts => {
    if (ACTION_PLAN_NAMESPACES.has(namespace)) {
        return "actionPlan";
    }

    return namespace as keyof TokenResolverContexts;
};

const resolveCalcFact = (token: string, calcFacts: CalcFactMap | undefined): string | undefined => {
    if (!calcFacts || !token.startsWith("calc.")) {
        return undefined;
    }

    return calcFacts[token]?.formattedValue;
};

export const buildTokenResolverContexts = (input?: TokenResolverContextsInput): TokenResolverContexts => {
    const calcFacts = input?.calc ?? input?.calcFacts ?? {};

    return {
        user: input?.user || {},
        company: input?.company || {},
        campaign: input?.campaign || {},
        actionPlan: input?.actionPlan || input?.actionsPlan || {},
        strategyData: input?.strategyData || {},
        scores: input?.scores || {},
        calc: calcFacts,
    };
};

export const processTemplateTokens = (
    text: string | undefined,
    contextsInput: TokenResolverContextsInput,
    optionsInput?: ProcessTokenOptions,
): string => {
    if (!text) return "";

    const contexts = buildTokenResolverContexts(contextsInput);
    const options: ProcessTokenOptions = {
        precision: optionsInput?.precision ?? 3,
        missingActionPlanValue: optionsInput?.missingActionPlanValue ?? "",
    };

    const tokenMatchContext: TokenMatchContext = {
        locale: resolveLocale(),
        timeZone: resolveTimeZone(),
    };

    return text.replaceAll(TOKEN_REGEX, (match, doubleBracedToken, singleBracedToken) => {
        const token = String(doubleBracedToken ?? singleBracedToken ?? "").trim();
        if (token === "") {
            return match;
        }

        try {
            const calcValue = resolveCalcFact(token, contexts.calc);
            if (calcValue !== undefined) {
                return calcValue;
            }

            const computedValue = resolveComputedToken(token, contexts, tokenMatchContext, options);
            if (computedValue !== undefined) {
                return computedValue;
            }

            const [rawNamespace, ...pathParts] = token.split(".");
            const namespace = normalizeNamespace(rawNamespace);
            const path = pathParts.join(".");
            const normalizedPath = normalizeArrayIndexPlaceholders(path);
            const namespaceContext = contexts[namespace as keyof TokenResolverContexts];
            if (!namespaceContext) {
                return match;
            }

            const value = path === "" ? namespaceContext : get(namespaceContext, normalizedPath);
            if (typeof value === "number") {
                return formatGenericNumber(value, options.precision ?? 3, tokenMatchContext.locale);
            }

            return value !== undefined && value !== null ? String(value) : match;
        } catch {
            return match;
        }
    });
};
